import type {
  EnrichmentAttributeGroup,
  EnrichmentAttributeOption,
} from '~/components/common/enrichment-run-panel';

import type { MetaAttributeGroupId, MetaCatalogGroup } from '../types/meta-catalog.types';

/**
 * Group id → display heading, mirroring the Google catalogue's `group_name`
 * ("Content (Generated Copy)"). Meta's wire carries only the id and the
 * subtitle-style `group_label`, so the heading half lives here until the meta
 * agent serves a `group_name` of its own (backend follow-up). Keyed on the
 * closed `Group` literal — a new wire group id fails the type first, so this
 * map cannot silently miss one.
 */
const META_GROUP_NAMES: Readonly<Record<MetaAttributeGroupId, string>> = {
  CONTENT: 'Content',
  DESCRIPTIVE: 'Descriptive',
  STRUCTURAL: 'Structural',
  PASSTHROUGH: 'Pass-Through',
  SEARCH: 'Search Attributes',
};

/**
 * Adapts the Meta catalogue into run-panel attribute groups. The wire's
 * `selectable` / `control_channel` flags are the source of truth (agent-services
 * #406 gave Meta enrichment `requested_fields`, superseding #402's
 * disclosure-only decision). Mirrors the GMC run panel: `requested_fields`
 * attributes are real switches defaulting ON ("everything is on by default"),
 * the non-selectable enriched fields (title / description / google_product_category)
 * render as locked always-on pills via `mandatory`, and the two `enrichment_config`
 * post-steps default OFF (a run-cost decision) with the API's `requires` driving
 * the phrase→search lock.
 *
 * Creation and delivery share this shape: Meta delivery now honours a per-field
 * selection, so `useDeliveryAttributeGroups` narrows these groups to the run's
 * enriched set and makes every survivor deselectable — no read-only disclosure.
 *
 * Groups that end up empty are omitted rather than rendered as `0/0` cards,
 * matching the Google adapter.
 */
export function adaptMetaCatalog(groups: readonly MetaCatalogGroup[]): EnrichmentAttributeGroup[] {
  const selectableGroups: EnrichmentAttributeGroup[] = [];

  for (const group of groups) {
    // Stable wire id as the slug (matching the Google adapter) — display text
    // must never be an identity, that's what broke the old config-keyed rules.
    const slug = group.id;
    const name = META_GROUP_NAMES[group.id];
    // Subtitle omitted when it would duplicate the heading — the SEARCH group's
    // case, so it renders as a bare "Search Attributes" exactly like GMC.
    const subLabel = group.label !== name ? group.label : undefined;

    const attributes: EnrichmentAttributeOption[] = [];
    for (const a of group.attributes.filter((attr) => !attr.selectable)) {
      // Always enriched and non-removable at creation — same treatment as GMC's
      // title/description/GPC: locked on, survives Clear all.
      attributes.push({ key: a.key, label: a.label, mandatory: true, defaultSelected: true });
    }
    for (const a of group.attributes.filter((attr) => attr.selectable)) {
      const requiresKey = a.requires[0];
      attributes.push({
        key: a.key,
        label: a.label,
        ...(requiresKey !== undefined ? { requiresKey } : {}),
        // Content fields start ON like GMC; post-steps start OFF everywhere.
        defaultSelected: a.controlChannel === 'requested_fields',
      });
    }

    if (attributes.length > 0) {
      selectableGroups.push({
        slug,
        label: name,
        ...(subLabel !== undefined ? { subLabel } : {}),
        attributes,
      });
    }
  }

  return selectableGroups;
}
